// Affiliate capture: when the visitor arrives with the affiliate URL
// parameter (?blogger=name by default, configurable in Theme Settings),
// store its value in localStorage so the Gravity Forms Affiliate field can
// attach it to form submissions. Last-touch: a new visit with the parameter
// overwrites the stored value; the value expires after the configured
// attribution window. Stored lowercase so routing rules stay predictable.
//
// The moment of the click is stored alongside the value and submitted with
// it, so every entry shows how long before the request the visitor arrived
// through the affiliate link.
const STORAGE_KEY = 'affiliate';
const VALUE_SELECTOR = '.gf-affiliate__input';
const CLICKED_SELECTOR = '.gf-affiliate__clicked';

/**
 * Read the stored affiliate, dropping it when it has expired.
 *
 * @param {number} days The configured attribution window, used to derive the
 *                      click moment for values stored before it was recorded.
 * @return {{value: string, clicked: number}|null}
 */
function read(days) {
	let data;

	try {
		const raw = localStorage.getItem(STORAGE_KEY);

		if (!raw) {
			return null;
		}

		data = JSON.parse(raw);
	} catch (e) {
		return null;
	}

	if (!data || typeof data.value !== 'string' || !data.value) {
		return null;
	}

	if (!data.expires || Date.now() > data.expires) {
		try {
			localStorage.removeItem(STORAGE_KEY);
		} catch (e) {
			// Storage unavailable: nothing to clean up.
		}

		return null;
	}

	// Values stored before the click moment was recorded: derive it from the
	// expiry, which is the click moment plus the attribution window.
	const clicked = data.clicked || data.expires - days * 24 * 60 * 60 * 1000;

	return { value: data.value, clicked };
}

/**
 * Store the affiliate from the URL parameter, if present.
 *
 * @param {string} param The affiliate URL parameter name.
 * @param {number} days  The attribution window in days.
 */
function capture(param, days) {
	const value = (new URLSearchParams(window.location.search).get(param) || '').trim();

	if (!value) {
		return;
	}

	const now = Date.now();

	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify({
			value: value.toLowerCase().slice(0, 190),
			clicked: now,
			expires: now + days * 24 * 60 * 60 * 1000,
		}));
	} catch (e) {
		// Storage unavailable: attribution is skipped.
	}
}

/**
 * Fill the hidden inputs of every Affiliate field on the page.
 *
 * @param {number} days The attribution window in days.
 */
function fill(days) {
	const inputs = document.querySelectorAll(VALUE_SELECTOR);

	if (!inputs.length) {
		return;
	}

	const data = read(days);

	inputs.forEach((input) => {
		input.value = data ? data.value : '';
	});

	document.querySelectorAll(CLICKED_SELECTOR).forEach((input) => {
		input.value = data ? new Date(data.clicked).toISOString() : '';
	});
}

export default function affiliate(settings) {
	const param = settings && settings.affiliate_parameter;

	if (!param) {
		return;
	}

	const days = parseInt(settings.affiliate_expiry_days, 10) || 90;

	capture(param, days);
	fill(days);

	// Gravity Forms replaces the form markup after an AJAX submission or a
	// multi-page step, which wipes the values filled above.
	if (window.jQuery) {
		window.jQuery(document).on('gform_post_render', () => fill(days));
	}

	document.addEventListener('gform_post_render', () => fill(days));
	document.addEventListener('gform/postRender', () => fill(days));

	if ('loading' === document.readyState) {
		document.addEventListener('DOMContentLoaded', () => fill(days));
	}
}
